import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import RequisitionForm, { type SaveIntent } from './RequisitionForm';
import RequestFormHelp from './RequestFormHelp';
import { errorMessage, useRequisitionsApi, type DraftInput, type RequisitionDetail } from './requisitionsApi';

const NewRequisitionPage: React.FC = () => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const history = useHistory();
  const confirm = useConfirm();
  const [saving, setSaving] = useState<SaveIntent | null>(null);

  const save = async (draft: DraftInput, intent: SaveIntent) => {
    setSaving(intent);
    let created: RequisitionDetail;
    try {
      created = await reqApi.create(draft);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the request. Check your connection and try again.'));
      setSaving(null);
      return;
    }
    if (intent === 'draft') {
      toast.success(`Saved ${created.req_number} as a draft.`);
      history.push(`/requisitions/${created.id}`);
      return;
    }
    try {
      const sent = await reqApi.submit(created.id);
      toast.success(`${sent.req_number} is with the approvers.`);
    } catch (err) {
      // The draft exists; the send did not land. Its page has the Send button.
      toast.error(
        `Saved ${created.req_number} as a draft, but it was not sent. ${errorMessage(err, 'Try Send for approval again.')}`
      );
    }
    history.push(`/requisitions/${created.id}`);
  };

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        back={{ to: "/requisitions", label: "Requests" }}
        title="New request"
        helpTitle="About requests"
        help={<RequestFormHelp />}
        lede="Ask for something to be bought. An approver says yes, then a buyer orders it."
      />
      <div className="max-w-4xl">
      <RequisitionForm
        saving={saving}
        onSave={save}
        onCancel={async () => {
          const ok = await confirm({
            title: 'Discard this request?',
            message: 'Nothing you typed has been saved.',
            confirmLabel: 'Discard',
            cancelLabel: 'Keep editing',
            tone: 'danger',
          });
          if (ok) history.push('/requisitions');
        }}
        cancelLabel="Discard"
      />
      </div>
    </PageLayout>
  );
};

export default NewRequisitionPage;
